import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatApiError } from "@/lib/api";

export const SLOTS = { matin: "Matin", midi: "Midi", apres_midi: "Après-midi", soir: "Soir" };
const day = (d) => new Date(d).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });

const ProList = ({ cid, onPick }) => {
  const { data, isLoading } = useQuery({ queryKey: ["eq-pros", cid], queryFn: () => api.get(`/business/companies/${cid}/equipment-pros`).then((r) => r.data) });
  if (isLoading) return <Skeleton className="h-40 rounded-xl" />;
  if (!data.length) return <p className="text-sm text-muted-foreground" data-testid="eq-pros-empty">Aucun réparateur partenaire CASHMY disponible pour le moment.</p>;
  return (
    <ul className="space-y-2" data-testid="eq-pros-list">{data.map((o) => (
      <li key={o.id}><button type="button" onClick={() => onPick(o)} className="lift w-full rounded-xl border border-border bg-background p-3 text-left text-sm hover:border-primary" data-testid={`eq-pro-${o.id}`}>
        <div className="flex items-center gap-2"><b className="flex-1">{o.name}</b>{o.rating && <span className="flex items-center gap-0.5 text-xs font-bold"><Star className="h-3 w-3 fill-[#e2b852] text-[#e2b852]" />{o.rating}</span>}</div>
        <div className="text-xs text-muted-foreground">{[o.city, (o.specialties || []).slice(0, 3).join(", ")].filter(Boolean).join(" · ")}</div>
      </button></li>))}</ul>
  );
};

const Agenda = ({ cid, org, item, reason, onBack, onBooked }) => {
  const { data, isLoading } = useQuery({ queryKey: ["eq-agenda", cid, org.id], staleTime: 0, queryFn: () => api.get(`/business/companies/${cid}/equipment-pros/${org.id}/agenda`).then((r) => r.data) });
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState("");
  const qc = useQueryClient();
  const book = async () => {
    try {
      await api.post(`/business/companies/${cid}/equipment/${item.id}/pro`, { org_id: org.id, day: pick[0], slot: pick[1], reason, note: note || null });
      qc.invalidateQueries({ queryKey: ["eq-agenda"] });
      toast.success(`Rendez-vous demandé chez ${org.name}`); onBooked();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid="eq-agenda-back"><ArrowLeft className="h-3 w-3" />Autres réparateurs</button>
      <p className="text-sm"><b>{org.name}</b>{org.phone ? ` · ${org.phone}` : ""}</p>
      {isLoading ? <Skeleton className="h-40 rounded-xl" /> : (
        <div className="max-h-64 space-y-1 overflow-y-auto pr-1" data-testid="eq-agenda">{data.days.map((d) => (
          <div key={d.date} className="grid grid-cols-[5.5rem_1fr] items-center gap-2 text-xs"><span className="font-semibold capitalize">{day(d.date)}</span>
            <div className="grid grid-cols-4 gap-1">{Object.entries(SLOTS).map(([s, l]) => {
              const on = pick?.[0] === d.date && pick?.[1] === s;
              return <button key={s} type="button" disabled={!d.slots[s]} onClick={() => setPick([d.date, s])} data-testid={`eq-slot-${d.date}-${s}`}
                className={`truncate rounded-md border px-0.5 py-1 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-30 ${on ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"}`}>{l}</button>;
            })}</div></div>))}</div>)}
      <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder={reason === "panne" ? "Décrivez la panne" : "Précisions (facultatif)"} data-testid="eq-book-note" />
      <Button className="w-full rounded-full" disabled={!pick} onClick={book} data-testid="eq-book-submit">Réserver ce créneau</Button>
    </div>
  );
};

export const EquipmentPros = ({ company, item, reason, onClose, onBooked }) => {
  const [org, setOrg] = useState(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="eq-pros-dialog">
        <DialogHeader><DialogTitle>{reason === "panne" ? "Panne" : "Entretien"} · {item.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Réparateurs partenaires CASHMY vérifiés, avec leurs créneaux libres sur 14 jours.</p>
        {org ? <Agenda cid={company.id} org={org} item={item} reason={reason} onBack={() => setOrg(null)} onBooked={onBooked} /> : <ProList cid={company.id} onPick={setOrg} />}
      </DialogContent>
    </Dialog>
  );
};
